import React, { useState, useEffect } from 'react';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, User, Phone, MapPin, ShoppingBag, Clock, 
  Heart, Star, TrendingUp, Calendar, Receipt,
  ChevronRight, AlertCircle, ThumbsUp, ThumbsDown, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const CustomerHistoryLookup = ({ storeId, isOpen, onClose, onSelectCustomer }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [favoriteItems, setFavoriteItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [allOrders, setAllOrders] = useState([]);
  const [walletInfo, setWalletInfo] = useState(null);

  useEffect(() => {
    if (isOpen && storeId) {
      loadAllOrders();
    }
  }, [isOpen, storeId]);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      searchCustomers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const loadAllOrders = async () => {
    try {
      // Raised from 500 - with the full real order history now merged in
      // (2,144+ orders), a low cap meant older customers silently vanished
      // from search entirely. This comfortably covers current volume with
      // room to grow.
      const response = await apiClient.get(`/orders?store_id=${storeId}&limit=5000`);
      setAllOrders(response.data || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const searchCustomers = () => {
    setIsSearching(true);
    
    // Extract unique customers from orders
    const customerMap = new Map();
    allOrders.forEach(order => {
      if (order.customer?.phone) {
        const key = order.customer.phone;
        const existing = customerMap.get(key);
        if (existing) {
          existing.orderCount++;
          existing.totalSpent += order.total || 0;
          if (new Date(order.created_at) > new Date(existing.lastOrderDate)) {
            existing.lastOrderDate = order.created_at;
          }
        } else {
          customerMap.set(key, {
            name: order.customer.name || 'Unknown',
            phone: order.customer.phone,
            address: order.customer.address || '',
            orderCount: 1,
            totalSpent: order.total || 0,
            lastOrderDate: order.created_at,
            firstOrderDate: order.created_at
          });
        }
      }
    });

    // Filter by search query
    const query = searchQuery.toLowerCase();
    const results = Array.from(customerMap.values()).filter(customer => 
      customer.name?.toLowerCase().includes(query) ||
      customer.phone?.includes(query) ||
      customer.address?.toLowerCase().includes(query)
    );

    setSearchResults(results.slice(0, 10));
    setIsSearching(false);
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setWalletInfo(null);
    
    // Get all orders for this customer
    const orders = allOrders.filter(o => o.customer?.phone === customer.phone);
    orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setCustomerOrders(orders);

    // Calculate stats
    const stats = calculateCustomerStats(orders);
    setCustomerStats(stats);

    // Calculate favorite items
    const favorites = calculateFavoriteItems(orders);
    setFavoriteItems(favorites);

    // Loyalty wallet - view-only for staff, redemption only ever happens
    // through the customer's own account when ordering online.
    try {
      const walletRes = await apiClient.get('/customers/wallet', { params: { phone: customer.phone } });
      setWalletInfo(walletRes.data.wallet);
    } catch {
      setWalletInfo(null);
    }
  };

  const calculateCustomerStats = (orders) => {
    if (orders.length === 0) return null;

    const totalOrders = orders.length;
    const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    const avgOrderValue = totalSpent / totalOrders;
    
    // Order frequency
    const dates = orders.map(o => new Date(o.created_at));
    const daysBetween = [];
    for (let i = 0; i < dates.length - 1; i++) {
      const diff = (dates[i] - dates[i + 1]) / (1000 * 60 * 60 * 24);
      daysBetween.push(diff);
    }
    const avgDaysBetweenOrders = daysBetween.length > 0 
      ? daysBetween.reduce((a, b) => a + b, 0) / daysBetween.length 
      : 0;

    // Order type distribution
    const orderTypes = {};
    orders.forEach(o => {
      orderTypes[o.order_type] = (orderTypes[o.order_type] || 0) + 1;
    });

    // Payment preference
    const paymentMethods = {};
    orders.forEach(o => {
      const method = o.payment_method || 'CASH';
      paymentMethods[method] = (paymentMethods[method] || 0) + 1;
    });

    // Time preference
    const orderHours = orders.map(o => new Date(o.created_at).getHours());
    const avgHour = orderHours.reduce((a, b) => a + b, 0) / orderHours.length;
    let timePreference = 'Afternoon';
    if (avgHour < 12) timePreference = 'Morning';
    else if (avgHour >= 18) timePreference = 'Evening';

    return {
      totalOrders,
      totalSpent,
      avgOrderValue,
      avgDaysBetweenOrders: Math.round(avgDaysBetweenOrders),
      orderTypes,
      paymentMethods,
      timePreference,
      firstOrder: orders[orders.length - 1]?.created_at,
      lastOrder: orders[0]?.created_at,
      isRegular: totalOrders >= 5,
      isVIP: totalSpent >= 10000
    };
  };

  const calculateFavoriteItems = (orders) => {
    const itemCounts = {};
    
    orders.forEach(order => {
      (order.items || []).forEach(item => {
        const key = item.menu_item_name;
        if (itemCounts[key]) {
          itemCounts[key].count += item.qty;
          itemCounts[key].totalSpent += item.price * item.qty;
        } else {
          itemCounts[key] = {
            name: item.menu_item_name,
            count: item.qty,
            totalSpent: item.price * item.qty,
            price: item.price
          };
        }
      });
    });

    return Object.values(itemCounts)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const handleUseCustomer = () => {
    if (selectedCustomer && onSelectCustomer) {
      onSelectCustomer({
        name: selectedCustomer.name,
        phone: selectedCustomer.phone,
        address: selectedCustomer.address
      });
      toast.success(`Customer ${selectedCustomer.name} selected!`);
      onClose();
    }
  };

  const getTimeSince = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
    if (diff < 365) return `${Math.floor(diff / 30)} months ago`;
    return `${Math.floor(diff / 365)} years ago`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" /> Customer Lookup & Order History
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, phone number, or address..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Search Results */}
          {searchQuery.length >= 3 && !selectedCustomer && (
            <Card>
              <CardContent className="p-2">
                {isSearching ? (
                  <p className="text-center py-4 text-gray-500">Searching...</p>
                ) : searchResults.length === 0 ? (
                  <p className="text-center py-4 text-gray-500">No customers found</p>
                ) : (
                  <ScrollArea className="max-h-60">
                    {searchResults.map((customer, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer rounded-lg"
                        onClick={() => handleSelectCustomer(customer)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-purple-100 p-2 rounded-full">
                            <User className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-gray-500">{customer.phone}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{customer.orderCount} orders</p>
                          <p className="text-xs text-gray-500">{formatCurrency(customer.totalSpent)}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      </div>
                    ))}
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          )}

          {/* Customer Details */}
          {selectedCustomer && (
            <div className="space-y-4">
              {/* Customer Info Card */}
              <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-purple-500 p-3 rounded-full">
                        <User className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-bold">{selectedCustomer.name}</h3>
                          {customerStats?.isVIP && (
                            <Badge className="bg-amber-500"><Star className="h-3 w-3 mr-1" /> VIP</Badge>
                          )}
                          {customerStats?.isRegular && !customerStats?.isVIP && (
                            <Badge className="bg-blue-500"><Heart className="h-3 w-3 mr-1" /> Regular</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {selectedCustomer.phone}</span>
                          {selectedCustomer.address && (
                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {selectedCustomer.address}</span>
                          )}
                        </div>
                        {walletInfo && (
                        <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full text-xs font-semibold" data-testid="customer-lookup-wallet">
                            <Coins className="h-3.5 w-3.5" />
                            {walletInfo.total_balance} pts total
                            {walletInfo.redeemable_now > 0 ? (
                              <span> - {walletInfo.redeemable_now} redeemable (Rs{walletInfo.redeemable_value_rupees})</span>
                            ) : walletInfo.total_balance > 0 ? (
                              <span className="text-amber-600 font-normal"> - 0 redeemable today</span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setSelectedCustomer(null)}>
                        Back to Search
                      </Button>
                      <Button className="bg-green-500" onClick={handleUseCustomer}>
                        Use This Customer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid */}
              {customerStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card>
                    <CardContent className="p-3 text-center">
                      <ShoppingBag className="h-5 w-5 mx-auto mb-1 text-blue-500" />
                      <p className="text-2xl font-bold">{customerStats.totalOrders}</p>
                      <p className="text-xs text-gray-500">Total Orders</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-500" />
                      <p className="text-2xl font-bold">{formatCurrency(customerStats.totalSpent)}</p>
                      <p className="text-xs text-gray-500">Total Spent</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Receipt className="h-5 w-5 mx-auto mb-1 text-orange-500" />
                      <p className="text-2xl font-bold">{formatCurrency(customerStats.avgOrderValue)}</p>
                      <p className="text-xs text-gray-500">Avg Order</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Clock className="h-5 w-5 mx-auto mb-1 text-purple-500" />
                      <p className="text-2xl font-bold">{customerStats.avgDaysBetweenOrders || '-'}</p>
                      <p className="text-xs text-gray-500">Days Between Orders</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Favorite Items - LIKES */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4 text-green-500" /> Favorite Items (Likes)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-48">
                      {favoriteItems.length === 0 ? (
                        <p className="text-center py-4 text-gray-500 text-sm">No order history</p>
                      ) : (
                        <div className="divide-y">
                          {favoriteItems.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between px-4 py-2">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{idx < 3 ? ['#1', '#2', '#3'][idx] : `${idx + 1}.`}</span>
                                <div>
                                  <p className="font-medium text-sm">{item.name}</p>
                                  <p className="text-xs text-gray-500">{formatCurrency(item.price)}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-sm">{item.count}x</p>
                                <p className="text-xs text-gray-500">{formatCurrency(item.totalSpent)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Preferences */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" /> Customer Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {customerStats && (
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Order Type:</span>
                          <span className="font-medium">
                            {Object.entries(customerStats.orderTypes)
                              .sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Payment:</span>
                          <span className="font-medium">
                            {Object.entries(customerStats.paymentMethods)
                              .sort((a, b) => b[1] - a[1])[0]?.[0] || 'CASH'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Prefers:</span>
                          <span className="font-medium">{customerStats.timePreference}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">First Order:</span>
                          <span className="font-medium">{getTimeSince(customerStats.firstOrder)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Last Order:</span>
                          <span className="font-medium">{getTimeSince(customerStats.lastOrder)}</span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Order History */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" /> Order History ({customerOrders.length} orders)
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-48">
                    {customerOrders.length === 0 ? (
                      <p className="text-center py-4 text-gray-500 text-sm">No orders found</p>
                    ) : (
                      <div className="divide-y">
                        {customerOrders.map((order, idx) => (
                          <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold">#{order.order_number}</span>
                                <Badge variant="outline" className="text-[10px]">{order.order_type}</Badge>
                                <Badge 
                                  className={`text-[10px] ${
                                    order.status === 'DELIVERED' || order.status === 'SERVED' 
                                      ? 'bg-green-500' 
                                      : order.status === 'CANCELLED' 
                                        ? 'bg-red-500' 
                                        : 'bg-blue-500'
                                  }`}
                                >
                                  {order.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {(order.items || []).map(i => `${i.qty}x ${i.menu_item_name}`).join(', ')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{formatCurrency(order.total)}</p>
                              <p className="text-xs text-gray-500">{getTimeSince(order.created_at)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerHistoryLookup;
